/**
 * Canonical metric metadata — the single source of truth the UI reads for
 * friendly labels, units, default aggregation, and the semantic color each
 * vital wears everywhere it appears (tiles, sparklines, trend series).
 *
 * Colors are drawn from the validated data-viz categorical palette. Each metric
 * is pinned to a fixed slot so a series' color follows the *entity*, never its
 * position in a selection — toggling one series never repaints the survivors.
 */

export type MetricAgg = 'avg' | 'min' | 'max' | 'sum';

export interface MetricMeta {
  /** Canonical metric_type as stored by the backend. */
  type: string;
  /** Human label for legends, checkboxes, tile captions. */
  label: string;
  /** Display unit (empty string for unitless counts like steps). */
  unit: string;
  /** Default aggregation for bucketed reads (sum for cumulative counts). */
  agg: MetricAgg;
  /** Categorical palette slot, 1-based (see palette below). */
  slot: number;
}

/**
 * Validated data-viz categorical palette (light + dark steps), by slot.
 * Slot ordering is the CVD-safety mechanism — do not reorder.
 */
export const PALETTE = {
  light: ['#2a78d6', '#1baf7a', '#eda100', '#008300', '#4a3aa7', '#e34948', '#e87ba4', '#eb6834'],
  dark: ['#3987e5', '#199e70', '#c98500', '#008300', '#9085e9', '#e66767', '#d55181', '#d95926'],
} as const;

/** Resolve a 1-based slot to its hex for the active mode. */
export function slotColor(slot: number, mode: 'light' | 'dark'): string {
  const ramp = PALETTE[mode];
  return ramp[(slot - 1) % ramp.length]!;
}

/**
 * Ordered so the default color assignment (slot 1..N) is pleasant and the
 * highest-signal daily vitals (resting HR, HRV, weight, sleep) get distinct,
 * memorable hues.
 */
export const METRICS: readonly MetricMeta[] = [
  { type: 'resting_hr', label: 'Resting HR', unit: 'bpm', agg: 'avg', slot: 6 },
  { type: 'heart_rate', label: 'Heart Rate', unit: 'bpm', agg: 'avg', slot: 6 },
  { type: 'hrv_sdnn', label: 'HRV (SDNN)', unit: 'ms', agg: 'avg', slot: 5 },
  { type: 'weight_kg', label: 'Weight', unit: 'kg', agg: 'avg', slot: 1 },
  { type: 'body_fat_pct', label: 'Body Fat', unit: '%', agg: 'avg', slot: 7 },
  { type: 'vo2max', label: 'VO₂ Max', unit: 'ml/kg·min', agg: 'avg', slot: 4 },
  { type: 'spo2', label: 'Blood Oxygen', unit: '%', agg: 'avg', slot: 2 },
  { type: 'respiratory_rate', label: 'Respiratory Rate', unit: 'br/min', agg: 'avg', slot: 2 },
  { type: 'steps', label: 'Steps', unit: '', agg: 'sum', slot: 4 },
  { type: 'active_calories', label: 'Active Calories', unit: 'kcal', agg: 'sum', slot: 3 },
  { type: 'basal_calories', label: 'Basal Calories', unit: 'kcal', agg: 'sum', slot: 3 },
  { type: 'distance_m', label: 'Distance', unit: 'm', agg: 'sum', slot: 2 },
  { type: 'flights_climbed', label: 'Flights Climbed', unit: '', agg: 'sum', slot: 4 },
  { type: 'exercise_minutes', label: 'Exercise Time', unit: 'min', agg: 'sum', slot: 3 },
  { type: 'lean_body_mass_kg', label: 'Lean Body Mass', unit: 'kg', agg: 'avg', slot: 1 },
  { type: 'cardio_recovery_bpm', label: 'Cardio Recovery', unit: 'bpm', agg: 'avg', slot: 6 },
  { type: 'daylight_minutes', label: 'Time in Daylight', unit: 'min', agg: 'sum', slot: 3 },
  { type: 'walking_hr_avg', label: 'Walking HR Avg', unit: 'bpm', agg: 'avg', slot: 6 },
  { type: 'headphone_audio_db', label: 'Headphone Audio', unit: 'dBASPL', agg: 'avg', slot: 8 },
  { type: 'caffeine_mg', label: 'Caffeine', unit: 'mg', agg: 'sum', slot: 8 },
  { type: 'water_ml', label: 'Water', unit: 'mL', agg: 'sum', slot: 2 },
  { type: 'alcohol_units', label: 'Alcohol', unit: 'units', agg: 'sum', slot: 7 },
  { type: 'work_hours', label: 'Work Hours', unit: 'h', agg: 'sum', slot: 5 },
  { type: 'bp_systolic', label: 'BP Systolic', unit: 'mmHg', agg: 'avg', slot: 6 },
  { type: 'bp_diastolic', label: 'BP Diastolic', unit: 'mmHg', agg: 'avg', slot: 1 },
  // Weather & air quality (source 'weather', one point/day at local noon).
  { type: 'weather_temp_c', label: 'Temperature', unit: '°C', agg: 'avg', slot: 3 },
  { type: 'weather_temp_max_c', label: 'Max Temp', unit: '°C', agg: 'max', slot: 8 },
  { type: 'weather_humidity_pct', label: 'Humidity', unit: '%', agg: 'avg', slot: 2 },
  { type: 'weather_pressure_hpa', label: 'Pressure', unit: 'hPa', agg: 'avg', slot: 1 },
  { type: 'weather_precip_mm', label: 'Precipitation', unit: 'mm', agg: 'sum', slot: 2 },
  { type: 'weather_aqi', label: 'Air Quality', unit: 'AQI', agg: 'avg', slot: 7 },
  { type: 'weather_pollen_grass', label: 'Grass Pollen', unit: 'gr/m³', agg: 'avg', slot: 4 },
  { type: 'weather_pollen_tree', label: 'Tree Pollen', unit: 'gr/m³', agg: 'avg', slot: 5 },
  { type: 'weather_pollen_weed', label: 'Weed Pollen', unit: 'gr/m³', agg: 'avg', slot: 8 },
] as const;

const BY_TYPE = new Map(METRICS.map((m) => [m.type, m]));

export function metricMeta(type: string): MetricMeta {
  return (
    BY_TYPE.get(type) ?? { type, label: type, unit: '', agg: 'avg', slot: 1 }
  );
}

/** Types offered in the Trends multi-select, in display order. */
export const TREND_TYPES: readonly string[] = METRICS.filter(
  (m) => m.type !== 'heart_rate', // resting_hr is the daily-vital proxy for the raw stream
).map((m) => m.type);

/**
 * Trends domain grouping. Each domain is one tab in the picker strip: tapping it
 * selects `default` and collapses the metric list to `types` (with an "all
 * metrics" escape hatch back to the full set). Sleep is a separate view, not a
 * metric domain, so it is not listed here.
 */
export interface Domain {
  key: string;
  label: string;
  /** Metrics shown when this domain is active, in display order. */
  types: readonly string[];
  /** Metric(s) auto-selected when the domain tab is tapped. */
  default: readonly string[];
}

export const DOMAINS: readonly Domain[] = [
  {
    key: 'activity',
    label: 'Activity',
    types: ['steps', 'distance_m', 'flights_climbed', 'exercise_minutes', 'active_calories', 'basal_calories'],
    default: ['steps'],
  },
  {
    key: 'heart',
    label: 'Heart',
    types: ['resting_hr', 'hrv_sdnn', 'heart_rate', 'vo2max', 'cardio_recovery_bpm', 'walking_hr_avg'],
    default: ['resting_hr', 'hrv_sdnn'],
  },
  {
    key: 'body',
    label: 'Body',
    types: ['weight_kg', 'body_fat_pct', 'lean_body_mass_kg'],
    default: ['weight_kg'],
  },
  {
    key: 'nutrition',
    label: 'Nutrition',
    types: ['caffeine_mg', 'water_ml', 'alcohol_units'],
    default: ['caffeine_mg'],
  },
  {
    key: 'environment',
    label: 'Environment',
    types: ['daylight_minutes', 'headphone_audio_db', 'spo2', 'respiratory_rate'],
    default: ['daylight_minutes'],
  },
  { key: 'work', label: 'Work', types: ['work_hours'], default: ['work_hours'] },
  {
    key: 'weather',
    label: 'Weather',
    types: [
      'weather_temp_c',
      'weather_temp_max_c',
      'weather_humidity_pct',
      'weather_pressure_hpa',
      'weather_precip_mm',
      'weather_aqi',
      'weather_pollen_grass',
      'weather_pollen_tree',
      'weather_pollen_weed',
    ],
    default: ['weather_temp_c'],
  },
] as const;

const DOMAIN_BY_KEY = new Map(DOMAINS.map((d) => [d.key, d]));

/** The domain for a key, or undefined for a non-domain tab (e.g. "sleep"). */
export function domainByKey(key: string): Domain | undefined {
  return DOMAIN_BY_KEY.get(key);
}

/**
 * The metric set a domain auto-selects when tapped. Falls back to the domain's
 * first metric if it declares no explicit default; empty for an unknown key.
 */
export function domainDefault(key: string): string[] {
  const domain = DOMAIN_BY_KEY.get(key);
  if (!domain) return [];
  if (domain.default.length > 0) return [...domain.default];
  return domain.types.length > 0 ? [domain.types[0]!] : [];
}
